/**
 * 時間範囲を表す値オブジェクト
 *
 * @description
 * 開始時間と終了時間のペアを表し、時間範囲の計算やバリデーションを提供する。
 * イミュータブルな値オブジェクトとして設計。
 *
 * @example
 * const range = createTimeRange('08:00', '12:00');
 * const duration = getDurationMinutes(range); // 240
 * const position = getPositionInDay(range, 7, 22); // { startPercent: 6.67, widthPercent: 26.67 }
 */
export interface TimeRange {
  readonly startTime: string; // HH:mm
  readonly endTime: string; // HH:mm
}

/**
 * TimeRange値オブジェクトを生成する
 *
 * @param {string} startTime - 開始時間 (HH:mm形式)
 * @param {string} endTime - 終了時間 (HH:mm形式)
 * @returns {TimeRange} TimeRange値オブジェクト
 * @throws {Error} 無効な時間形式の場合
 */
export const createTimeRange = (startTime: string, endTime: string): TimeRange => {
  if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
    throw new Error('無効な時間形式です。HH:mm形式で指定してください。');
  }
  return { startTime, endTime };
};

/**
 * 時間形式が有効かどうかを検証する
 *
 * @param {string} time - 検証する時間文字列
 * @returns {boolean} 有効な場合はtrue
 */
export const isValidTimeFormat = (time: string): boolean => {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
};

/**
 * 時間範囲の長さを分単位で取得する
 *
 * @param {TimeRange} range - 時間範囲
 * @returns {number} 分単位の長さ
 */
export const getDurationMinutes = (range: TimeRange): number => {
  const [startHour, startMin] = range.startTime.split(':').map(Number);
  const [endHour, endMin] = range.endTime.split(':').map(Number);
  return endHour * 60 + endMin - (startHour * 60 + startMin);
};

/**
 * 1日の時間範囲内での位置を計算する（タイムライン表示用）
 *
 * @param {TimeRange} range - 時間範囲
 * @param {number} dayStartHour - 1日の開始時間（例: 7）
 * @param {number} dayEndHour - 1日の終了時間（例: 22）
 * @returns {{ startPercent: number; widthPercent: number }} 開始位置と幅（パーセント）
 */
export const getPositionInDay = (
  range: TimeRange,
  dayStartHour: number,
  dayEndHour: number
): { startPercent: number; widthPercent: number } => {
  const totalMinutes = (dayEndHour - dayStartHour) * 60;
  const [startHour, startMin] = range.startTime.split(':').map(Number);
  const [endHour, endMin] = range.endTime.split(':').map(Number);

  const startMinutesFromDayStart = (startHour - dayStartHour) * 60 + startMin;
  const endMinutesFromDayStart = Math.min(
    (endHour - dayStartHour) * 60 + endMin,
    totalMinutes
  );

  const startPercent =
    Math.max(0, startMinutesFromDayStart / totalMinutes) * 100;
  const widthPercent =
    Math.max(
      0,
      (endMinutesFromDayStart - Math.max(0, startMinutesFromDayStart)) /
        totalMinutes
    ) * 100;

  return { startPercent, widthPercent };
};

/**
 * 時間文字列を分単位に変換する
 *
 * @param {string} time - 時間文字列 (HH:mm)
 * @returns {number} 分単位の時間
 */
export const timeToMinutes = (time: string): number => {
  const [hour, min] = time.split(':').map(Number);
  return hour * 60 + min;
};

/**
 * 分単位の時間を時間文字列に変換する
 *
 * @param {number} minutes - 分単位の時間
 * @returns {string} 時間文字列 (HH:mm)
 */
export const minutesToTime = (minutes: number): string => {
  const hour = Math.floor(minutes / 60);
  const min = minutes % 60;
  return `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
};
